import { useCallback, useEffect, useState } from 'react';

export type TradeDetailViewMode = 'comfortable' | 'compact' | 'list';

const STORAGE_KEY = 'tradeDetailViewMode';
const CHANGE_EVENT = 'tradeDetailViewModeChange';
const MOBILE_BREAKPOINT = 768;

const getDefault = (): TradeDetailViewMode => {
  if (typeof window === 'undefined') return 'comfortable';
  return window.innerWidth <= MOBILE_BREAKPOINT ? 'list' : 'comfortable';
};

const readMode = (): TradeDetailViewMode => {
  if (typeof window === 'undefined') return 'comfortable';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'comfortable' || stored === 'compact' || stored === 'list') {
    return stored;
  }
  return getDefault();
};

/**
 * Shared view-mode state for trade detail tables on /sending and /receiving.
 * Persists to localStorage and syncs across mounted instances within the same tab
 * via a custom event, so changing the mode in one TradeCard updates all others.
 */
export function useTradeDetailViewMode(): [TradeDetailViewMode, (mode: TradeDetailViewMode) => void] {
  const [mode, setMode] = useState<TradeDetailViewMode>(readMode);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<TradeDetailViewMode>).detail;
      if (detail) setMode(detail);
    };
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  }, []);

  const update = useCallback((next: TradeDetailViewMode) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    window.dispatchEvent(new CustomEvent<TradeDetailViewMode>(CHANGE_EVENT, { detail: next }));
  }, []);

  return [mode, update];
}

export const TRADE_DETAIL_VIEW_OPTIONS: { value: TradeDetailViewMode; label: string }[] = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
  { value: 'list', label: 'List' },
];
